import { useEffect, useMemo, useRef, useState } from 'react'
import { ClipboardList, Trash2, FileText, AlertTriangle, Lock, Users, RotateCcw } from 'lucide-react'
import { apiGet, cachedGet, patchJson } from '../../lib/api.js'
import { buildDocInfo, eligibleDoctorsFromTimetable } from '../../lib/doctorFilter.js'
import {
  sumLines, doctorName, paymentOf, isNoPay, TEAL, asArray, sortPayMethods,
} from '../../lib/billing.js'
import { asList, getDoctorImage } from '../../lib/chart-helpers.js'
import { nameInitial } from '../../lib/text.js'
import { isBillingDoc } from '../DocumentGenerator.jsx'
import {
  Drawer, DrawerFooter, ConfirmDialog, Button, Field, Select, SearchSelect, Textarea, Checkbox, Banner,
  useToast, fmtMoney, fmtDate, fmtDateText, bkkTodayISO,
} from '../../kit'
import { TREATMENT_WATCH, conflictFromError, conflictingFields, describeChange, pickExpected } from '../../lib/conflict.js'

/**
 * One visit, in full — the right-hand drawer behind a click on a treatment card.
 *
 * Deliberately NOT a billing surface: the lines live on the card, behind its
 * chevron, and only there. This drawer is the visit's OWN record — the money
 * totals for context, then วิธีชำระเงิน / auto fee / ประเภท / หมอ / notes, the
 * documents, and the way through to the chart.
 *
 * The drawer opens EDITABLE — there is no แก้ไข step. บันทึก only lights up once
 * something has actually changed, so an opened-and-closed drawer writes nothing
 * (every save re-runs autofeeBilling and fires realtime).
 *
 * What may be edited depends on whether the visit came from an appointment:
 *   linked   → วันที่ · สาขา · ประเภท · หมอ all locked; fix the appointment instead,
 *              or the treatment stops describing the booking it came from.
 *   unlinked → ประเภท and หมอ are editable (หมอ filtered to who actually had a
 *              shift for that branch+date+ประเภท). วันที่ and สาขา stay locked
 *              regardless: the record exists, and moving either rewrites which
 *              month and which branch the money belongs to.
 */
export default function TreatmentDrawer({
  patientId, t, chart, branch, pmById,
  canWrite, isManager, onChanged, onClose,
}) {
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [armDel, setArmDel] = useState(false)   // the delete ConfirmDialog is open
  const [deleting, setDeleting] = useState(false)
  const toast = useToast()

  const tid = t.id ?? t.treatment_id
  const lines = asArray(t.billing)
  /* Lab cases on this visit. api 153 drops the raw `lab[]` and leaves a count —
     addon 65 has NO active filter, so a voided lab still counts here and can
     grey the delete button on a visit the server would actually allow. That is
     the safe direction of the two (the proxy filters `lab.active` properly and
     is the authority); fixing it means one `&& $db.lab.active == true` in addon
     65, which also touches `lab_price` / `lab_name` and Bubble. */
  const labCount = Number(t.lab_count) || 0
  /* A written clinical note is the hard stop — no amount of tidying billing
     makes the visit deletable once a dentist has recorded what was done. This
     `chart` is the FULL record from /api/charts (api 153 unsets its own chart
     addon and leaves only consent counts), so `hx` is really here. Stricter
     than Xano api 151, which tests `hx.cc` alone and so would destroy a chart
     holding PE / Dx / Tx / canvas but no cc. */
  const hxCount = asArray(chart?.hx).length
  const s = sumLines(lines)
  const pay = paymentOf(t, pmById)
  const noPay = isNoPay(pay.key)
  const docName = doctorName(t)
  const docImg = getDoctorImage(t)
  const al = t.appointmentlist || t.appointmentList || {}
  const alLabel = [al.type || t.type, al.detail].filter(Boolean).join(' · ')
  const docs = asArray(chart?.document).filter((d) => isBillingDoc(d))
  // No payment method is only wrong when รวม (out-of-pocket) isn't 0 — see PayChip.
  const payRisk = noPay && Math.abs(s.patient) > 0.5

  const [detail, setDetail] = useState(String(t.detail ?? ''))
  const [remark, setRemark] = useState(String(t.remark ?? ''))
  const [payment, setPayment] = useState(() => (noPay ? '' : pay.name))
  const [autoFee, setAutoFee] = useState(t.auto_fee !== false)
  const [alId, setAlId] = useState(t.appointmentlist_id != null ? String(t.appointmentlist_id) : '')
  const [docId, setDocId] = useState(t.doctor_id != null ? String(t.doctor_id) : '')

  // A visit booked through an appointment takes วันที่/สาขา/ประเภท/หมอ FROM that
  // appointment. Editing them here would leave the treatment describing a booking
  // that no longer matches, so the appointment is the place to change them.
  const linked = Number(t.appointment_id) > 0

  /* THE ROW AS THIS DRAWER OPENED IT.
   *
   * `t` is re-derived from the page's list on every refetch, so a colleague's
   * write reaches this component as a NEW `t` while the inputs above — seeded
   * once by useState — keep what was loaded. That split is what made the drawer
   * dangerous: `dirty` compared the inputs against the FRESH row, so somebody
   * else's edit lit บันทึก on its own, and save() then wrote the stale text back.
   *
   * So the two questions are separated, and each is asked against the right row:
   *   did I change this?        → input vs `base`
   *   did someone else?         → `base` vs `t`   (the banner below)
   * The server asks the second one again at save time and refuses 409, because
   * it can see writes this page never heard about — Bubble's especially. */
  const [base, setBase] = useState(t)

  // Our OWN save is followed by a refetch that brings back what we just wrote.
  // Without this the new `t` would be compared against the pre-save baseline and
  // reported as somebody else's change.
  const rebase = useRef(false)
  useEffect(() => {
    if (rebase.current) { rebase.current = false; setBase(t) }
  }, [t])

  // What moved underneath this drawer while it sat open. Recomputed as `t`
  // arrives, so the warning appears while the user is still reading rather than
  // at บันทึก. `serverMoved` is the same question answered authoritatively by the
  // proxy on a 409 — preferred when present.
  const [serverMoved, setServerMoved] = useState(null)
  const moved = serverMoved ?? conflictingFields(base, t, TREATMENT_WATCH)

  const [refs, setRefs] = useState(null)   // {docInfo, types} — only for unlinked edits
  const [tt, setTt] = useState(null)       // that branch+date's timetable rows

  // วิธีชำระเงิน is same-day-only for ordinary staff: it splits df_trace from
  // df_nontrace in payroll and drives the auto card-fee line. The proxy enforces
  // this per request; this copy only keeps the control honest on screen.
  const txDay = String(t.date ?? '').slice(0, 10)
  const bkkToday = useMemo(() => bkkTodayISO(), [])
  const paymentLocked = !isManager && (!txDay || txDay !== bkkToday)
  const payOptions = useMemo(() => {
    const seen = new Set(); const list = []
    const add = (v) => { const n = String(v ?? '').trim(); if (n && !seen.has(n)) { seen.add(n); list.push(n) } }
    for (const name of (pmById?.values?.() || [])) add(name)
    if (!noPay) add(pay.name)
    return sortPayMethods(list)
  }, [pmById, pay.name, noPay])

  // Only an unlinked visit can repoint ประเภท/หมอ, so only it pays for the lookups.
  useEffect(() => {
    if (!canWrite || linked || refs) return
    Promise.allSettled([cachedGet('/api/doctors', { slim: 1 }), cachedGet('/api/appointment-types')])
      .then(([dr, ty]) => setRefs({
        docInfo: buildDocInfo(dr.status === 'fulfilled' ? asList(dr.value) : []),
        types: ty.status === 'fulfilled' ? asList(ty.value) : [],
      }))
  }, [canWrite, linked, refs])

  // The doctor list is "who actually had a shift at this branch on this day",
  // then narrowed to who may do this ประเภท — same source the booking form uses.
  useEffect(() => {
    if (!canWrite || linked || !txDay || !t.branch_id) return
    let alive = true
    apiGet('/api/appointments', { type: 'timetable', branch_id: t.branch_id, start_date: txDay, end_date: txDay })
      .then((r) => { if (alive) setTt(asList(r)) })
      .catch(() => { if (alive) setTt([]) })
    return () => { alive = false }
  }, [canWrite, linked, txDay, t.branch_id])

  const typeIndex = useMemo(() => {
    const byId = new Map(); const byType = new Map()
    for (const ty of refs?.types || []) {
      const rec = { id: String(ty.id), type: ty.type || 'อื่นๆ', detail: ty.detail || ty.type || '' }
      byId.set(rec.id, rec)
      if (!byType.has(rec.type)) byType.set(rec.type, [])
      byType.get(rec.type).push(rec)
    }
    return { byId, byType }
  }, [refs])

  const curType = typeIndex.byId.get(String(alId))?.type || ''
  const [typeName, setTypeName] = useState('')
  const activeType = typeName || curType
  const detailOpts = typeIndex.byType.get(activeType) || []

  const docOpts = useMemo(() => {
    const chosen = typeIndex.byId.get(String(alId))
    const list = eligibleDoctorsFromTimetable(tt || [], chosen ? { id: chosen.id, type: chosen.type, detail: chosen.detail } : null, refs?.docInfo || {})
    // The treatment's CURRENT doctor must stay selectable even if they no longer
    // have a shift that day — otherwise saving anything else would silently
    // reassign the visit to whoever happens to be first in the list.
    if (docId && !list.some((d) => d.id === String(docId))) {
      list.unshift({ id: String(docId), name: docName || `หมอ #${docId}`, img: docImg || '' })
    }
    return list
  }, [tt, alId, typeIndex, refs, docId, docName, docImg])

  /* Exactly the fields save() sends — so the button lights up only when a write
     would really change something.
     Compared against `base`, NOT against the live `t`: this asks "did the USER
     change it", and a colleague's write must not answer yes on their behalf.
     วิธีชำระเงิน is the one exception, still measured against `t` — it is not in
     TREATMENT_WATCH (the drawer holds a resolved NAME, the row an id, so the two
     cannot be compared) and it is same-day-pinned by the proxy anyway. */
  const dirty = (
    String(detail) !== String(base.detail ?? '')
    || String(remark) !== String(base.remark ?? '')
    || (!paymentLocked && String(payment) !== String(noPay ? '' : pay.name))
    || (isManager && autoFee !== (base.auto_fee !== false))
    || (!linked && !!alId && String(alId) !== String(base.appointmentlist_id ?? ''))
    || (!linked && isManager && !!docId && String(docId) !== String(base.doctor_id ?? ''))
  )

  /* Reset the form to what is stored — the drawer opens editable, so there is no
     "enter edit mode"; this is the undo.
     It is ALSO the conflict banner's ใช้ค่าล่าสุด: "what is stored" and "what the
     colleague just wrote" are the same row, so taking their version and undoing
     your own edits is one action, not two. Re-baselining is what clears the
     banner — without it the drawer would keep warning about a change it is now
     showing. */
  function reset() {
    setDetail(String(t.detail ?? '')); setRemark(String(t.remark ?? ''))
    setPayment(noPay ? '' : pay.name)
    setAutoFee(t.auto_fee !== false)
    setAlId(t.appointmentlist_id != null ? String(t.appointmentlist_id) : '')
    setDocId(t.doctor_id != null ? String(t.doctor_id) : '')
    setTypeName('')
    setErr('')
    setBase(t)
    setServerMoved(null)
  }

  /* `force` is the user answering the conflict banner with เขียนทับ: send no
     `expected`, so the proxy stops asking and the write lands. Deliberately an
     explicit second action — the first save is refused, the overwrite is chosen. */
  async function save(force = false) {
    setSaving(true); setErr('')
    try {
      /* ⚠️ ONLY WHAT THIS USER CHANGED.
         `detail` and `remark` used to be sent unconditionally, which is how an
         open drawer clobbered notes: it put back whatever was on screen when the
         drawer opened, on ANY save — including one that only touched วิธีชำระเงิน.
         Compared against `base` for the same reason `dirty` is: the question is
         what the user edited, not what the row now says. */
      const body = {}
      if (String(detail) !== String(base.detail ?? '')) body.detail = detail
      if (String(remark) !== String(base.remark ?? '')) body.remark = remark
      // Never rewrite a past day's method — the proxy would refuse it anyway,
      // and sending it would turn a clean save into a partial one. Same reasoning
      // for every other gated field: send it only when it may actually land.
      if (!paymentLocked && String(payment) !== String(noPay ? '' : pay.name)) body.paymentMethod = payment
      if (isManager && autoFee !== (base.auto_fee !== false)) body.auto_fee = autoFee
      if (!linked && alId && String(alId) !== String(base.appointmentlist_id ?? '')) {
        body.appointmentlist_id = Number(alId)
      }
      if (!linked && isManager && docId && String(docId) !== String(base.doctor_id ?? '')) {
        body.doctor_id = Number(docId)
      }

      // Nothing actually differs — `dirty` should already have kept the button
      // dark, so this is the belt to its braces rather than a path users reach.
      if (!Object.keys(body).length) { setSaving(false); reset(); return }

      // What this drawer believes the row still says. The proxy compares it to
      // the stored row and refuses 409 if it has moved — see lib/conflict.js.
      const expected = force ? null : pickExpected(base, TREATMENT_WATCH)
      if (expected) body.expected = expected

      const r = await patchJson(`/api/treatments/${tid}`, body)
      if (r?.refused_fields?.length) {
        // detail/remark DID land; keep the drawer open on the message rather
        // than dismissing it as a clean save.
        rebase.current = true
        onChanged?.()
        setErr(r.refused_detail || 'บางรายการแก้ไขไม่ได้')
        toast.error('บันทึกได้บางส่วน', { detail: r.refused_detail || 'บางรายการแก้ไขไม่ได้' })
        setSaving(false)
        return
      }
      setSaving(false)
      setServerMoved(null)
      rebase.current = true
      onChanged?.()
      toast.success('บันทึกแล้ว')
    } catch (e) {
      // A 409 from the lost-update gate is not an error to apologise for — it is
      // the drawer's own warning, arriving from the one place that can be sure.
      // `fields` empty would hide the banner AND leave the button reading บันทึก,
      // so the next click would re-send `expected` and 409 again forever. Only a
      // conflict we can actually show is shown as one.
      const c = conflictFromError(e)
      if (c?.stale && c.fields.length) { setServerMoved(c.fields); setSaving(false); return }
      const msg = humanErr(e)
      setErr(msg); setSaving(false)
      toast.error('บันทึกไม่สำเร็จ', { detail: msg })
    }
  }

  // The ConfirmDialog stays open (busy) while the request runs, so a second
  // click cannot send it twice; it closes on failure, or with the drawer on success.
  async function doDelete() {
    setDeleting(true); setErr('')
    try {
      // Soft delete, the house convention: the row survives for audit and
      // `type=patient` filters active == true, so the visit leaves the ledger.
      //
      // The proxy re-checks for live billing and answers 409 `treatment_has_billing`
      // — this is not belt-and-braces, it is the real check: `lines` was read when
      // the drawer opened, and a colleague can add a line while it sits there.
      // humanErr() surfaces the Thai `detail` it sends back.
      await patchJson(`/api/treatments/${tid}`, { active: false })
      onChanged?.()
      toast.success('ลบการรักษาแล้ว')
      onClose?.()
    } catch (e) {
      const msg = humanErr(e)
      setErr(msg); setDeleting(false); setArmDel(false)
      toast.error('ลบไม่สำเร็จ', { detail: msg })
    }
  }

  const payOpts = [{ value: '', label: '— ไม่ระบุ —' }, ...payOptions.map((n) => ({ value: n, label: n }))]
  const typeOpts = [...typeIndex.byType.keys()].sort((a, b) => a.localeCompare(b, 'th')).map((ty) => ({ value: ty, label: ty }))

  /* Manager-only, and REFUSED while the visit still has billing.
     or lab. Deleting writes `active:false` on the treatment and
     nothing else, so the lines would stay countable — ค้างชำระ,
     ตามเบิก and the RevenueReport list all filter on `billing.active`
     alone — and a lab case would be left pointing at a visit that no
     longer exists, exactly the orphan shape found in the 2026-08-16
     audit. Void them first and each gets its own log entry naming who
     did it; a cascade would move five rows on one confirmation with
     no trace. Enforced in functions/api/treatments/[id].js — this copy
     only says why, so nobody arms a delete and meets a 409. */
  const deleteBlocked = hxCount
    ? `ลบไม่ได้ — หมอบันทึก Chart ไว้แล้ว ${hxCount} รายการ`
    : (lines.length || labCount)
      ? `ลบไม่ได้ — ยังมี${lines.length ? ` Billing ${lines.length} รายการ` : ''}${lines.length && labCount ? ' และ' : ''}${labCount ? ` Lab ${labCount} รายการ` : ''} ให้ลบออกก่อน`
      : ''

  return (
    <Drawer
      onClose={onClose}
      width="lg"
      busy={saving || deleting}
      dirty={dirty}
      /* The audit line (#id · สร้าง · แก้ไข), pinned bottom-left by the kit. */
      record={t}
      recordId={tid}
      recordTable="treatment"
      title={`การรักษา ${fmtDateText(t.date, { weekday: true })}`}
      subtitle={(branch || docName || alLabel) ? (
        <span className="flex items-center gap-1.5 flex-wrap">
          {branch && <span>{branch}</span>}
          {docName && <>
            {branch && <span className="text-slate-300">·</span>}
            {docImg
              ? <img src={docImg} alt="" className="w-[18px] h-[18px] rounded-full object-cover" />
              : <span className="w-[18px] h-[18px] rounded-full bg-slate-100 text-slate-500 text-[9px] flex items-center justify-center">{nameInitial(docName)}</span>}
            <span className="text-slate-700 font-medium">{docName}</span>
          </>}
          {alLabel && <><span className="text-slate-300">·</span><span>{alLabel}</span></>}
        </span>
      ) : null}
      headerActions={(
        <Button variant="soft" size="sm" icon={ClipboardList}
          to={`/patient/${patientId}/chart/history/${tid}`} state={{ treatment: t, chart }}>
          เปิด Chart
        </Button>
      )}
      footer={(
        <DrawerFooter
          danger={isManager && (deleteBlocked ? (
            <span className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-400">
              <Lock size={13} className="flex-none text-slate-300" />{deleteBlocked}
            </span>
          ) : (
            <Button variant="danger-soft" size="sm" icon={Trash2} loading={deleting} disabled={saving}
              onClick={() => setArmDel(true)}>ลบ</Button>
          ))}
          /* คืนค่าเดิม only once something has actually changed — the drawer opens
             editable, so an always-live save button would invite no-op writes
             (each one re-runs autofeeBilling and fires realtime). */
          secondary={canWrite && dirty ? (
            <Button variant="ghost" size="sm" icon={RotateCcw} onClick={reset} disabled={saving}>คืนค่าเดิม</Button>
          ) : null}
          status={canWrite && !dirty ? 'ยังไม่มีการแก้ไข' : null}
          onCancel={onClose}
          cancelLabel={canWrite ? 'ยกเลิก' : 'ปิด'}
          onSave={canWrite ? () => save(moved.length > 0) : undefined}
          saving={saving}
          canSave={dirty}
          saveLabel={moved.length > 0 ? 'บันทึกทับ' : 'บันทึก'}
        />
      )}
    >
      <div className="space-y-4">
        {/* Money first — it is why this page exists. */}
        <section className="rounded-2xl bg-white border border-slate-200 p-4">
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <Stat label="รวมเบิก" value={s.total} strong />
            <Stat label="รวม" value={s.patient} color={TEAL} strong />
            <Stat label="เบิก" value={s.advance} color="#4f46e5" />
            <Stat label="ส่วนคลินิก" value={s.clinic} />
            <Stat label="ส่วนหมอ" value={s.doctor} />
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {/* Same rule as the card: ไม่ระบุ is only an error when รวม isn't 0.
                The chip carries the state instead of a second badge beside it. */}
            <span
              title={payRisk ? 'เก็บเงินจากคนไข้แล้วแต่ไม่ได้ระบุวิธีชำระ — ยอด “รวม” ควรเป็น 0 ถ้ายังไม่ได้เก็บเงิน' : undefined}
              className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12.5px] ring-1 ${
                payRisk ? 'bg-rose-50 text-rose-600 ring-rose-200 font-medium'
                  : noPay ? 'bg-slate-50 text-slate-500 ring-slate-200'
                    : 'bg-white text-slate-700 ring-slate-200'}`}>
              {payRisk && <AlertTriangle size={12} className="flex-none" />}
              วิธีชำระ: {pay.name || 'ไม่ระบุ'}
            </span>
            {t.auto_fee === false && (
              <span className="inline-flex items-center rounded-lg bg-slate-50 text-slate-500 ring-1 ring-slate-200 px-2 py-1 text-[12.5px]">auto fee ปิด</span>
            )}
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-slate-200 p-4 space-y-3.5">
          <Field
            label={<span className="inline-flex items-center gap-1.5">วิธีชำระเงิน {paymentLocked && <Lock size={12} className="text-slate-400" />}</span>}
            hint={paymentLocked ? 'เปลี่ยนวิธีชำระเงินได้เฉพาะวันที่ทำการรักษา (เวลาไทย) — นอกจากนั้นต้องเป็นผู้จัดการ' : undefined}
          >
            <Select value={payment} onChange={setPayment} options={payOpts} placeholder="— ไม่ระบุ —"
              disabled={paymentLocked || !canWrite} />
          </Field>
          {/* auto_fee is MANAGER_ONLY in the proxy: switching it off stops the
              1.5% card fee and the insurance 1% line being maintained at all. */}
          {/* A Checkbox, not a Switch: it is part of this form and only lands on บันทึก. */}
          <Checkbox checked={autoFee} disabled={!isManager || !canWrite} onChange={setAutoFee}
            label="คิดค่าธรรมเนียมอัตโนมัติ (auto fee)"
            hint={`ปิดแล้วระบบจะไม่คิดค่าธรรมเนียมบัตร 1.5% และไม่ดูแลรายการหักประกัน 1% ให้อีก${!isManager ? ' · เฉพาะผู้จัดการ' : ''}`} />

          {!linked && (
            <>
              <Field label="ประเภทการรักษา">
                <SearchSelect value={activeType} onChange={(v) => { setTypeName(v); setAlId('') }}
                  options={typeOpts} disabled={!refs}
                  placeholder={refs ? '— เลือกประเภท —' : 'กำลังโหลด…'} searchPlaceholder="พิมพ์ชื่อประเภท…" />
              </Field>

              {activeType && (
                <Field label="รายละเอียดประเภท">
                  <SearchSelect value={alId} onChange={setAlId}
                    options={detailOpts.map((d) => ({ value: d.id, label: d.detail }))}
                    placeholder="— เลือกรายละเอียด —" />
                </Field>
              )}

              <Field
                label={<span className="inline-flex items-center gap-1.5">หมอ {!isManager && <Lock size={12} className="text-slate-400" />}</span>}
                hint={<>
                  {tt === null ? 'กำลังโหลดตารางเวรของวันนั้น…'
                    : `แสดงเฉพาะหมอที่ขึ้นเวรสาขานี้ในวันที่ ${fmtDate(txDay)} และทำรายการนี้ได้ (${docOpts.length} คน)`}
                  {!isManager && ' · เปลี่ยนหมอได้เฉพาะผู้จัดการ'}
                </>}
              >
                <SearchSelect value={docId} onChange={setDocId} disabled={!isManager}
                  options={docOpts.map((d) => ({ value: d.id, label: d.name }))}
                  placeholder="— เลือกหมอ —" searchPlaceholder="พิมพ์ชื่อหมอ…" />
              </Field>
            </>
          )}

          <Field label="รายละเอียด">
            <Textarea rows={2} value={detail} onChange={(e) => setDetail(e.target.value)} disabled={!canWrite} className="resize-y" />
          </Field>
          <Field label="หมายเหตุภายใน">
            <Textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} disabled={!canWrite} className="resize-y"
              placeholder="ไม่แสดงต่อคนไข้ — พนักงานและทันตแพทย์เห็น" />
          </Field>
        </section>

        {/* SOMEONE ELSE WROTE THIS ROW. Amber, not red: nothing has gone wrong
            yet, and the drawer is still usable — but บันทึก from here would
            replace their figure, so the choice is put in front of the user
            instead of being made for them. Auto-refreshing the inputs would be
            the other silent answer, and swapping values under a hand that is
            mid-edit is the worse of the two. */}
        {moved.length > 0 && (
          <Banner tone="warn" icon={Users} title="มีคนแก้ไขการรักษานี้ระหว่างที่หน้าต่างนี้เปิดอยู่"
            action={<Button variant="secondary" size="sm" onClick={reset} disabled={saving}>ใช้ค่าล่าสุด</Button>}>
            <ul className="list-disc ml-4 space-y-0.5">
              {moved.map((m) => <li key={m.field}>{describeChange(m)}</li>)}
            </ul>
            {dirty && <div className="mt-1 text-[12px]">หรือกด “บันทึกทับ” เพื่อใช้ค่าที่คุณแก้</div>}
          </Banner>
        )}

        {err && (
          <div className="flex items-start gap-1.5 rounded-xl bg-rose-50 border border-rose-200 p-3 text-[13px] text-rose-700" role="alert">
            <AlertTriangle size={14} className="flex-none mt-0.5" />{err}
          </div>
        )}

        {docs.length > 0 && (
          <section className="rounded-2xl bg-white border border-slate-200 p-4">
            <div className="text-[11.5px] uppercase tracking-wide text-slate-400 mb-2">เอกสารในเคสนี้</div>
            <div className="flex flex-wrap gap-1.5">
              {docs.map((doc, i) => {
                const location = doc.location || doc.file?.path || ''
                const href = location ? `/api/files/${encodeURIComponent(location).replace(/%2F/g, '/')}` : (doc.url || '')
                if (!href) return null
                return (
                  <a key={i} href={href} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 ring-1 ring-slate-200 px-2 py-1 text-[12px] text-slate-600 hover:bg-brand-50 hover:text-brand-700 hover:ring-brand-200 transition-colors">
                    <FileText size={12} />
                    <span className="truncate max-w-[240px]">{doc.doc_type || doc.name || 'เอกสาร'}</span>
                  </a>
                )
              })}
            </div>
          </section>
        )}

        {/* Where the visit CAME from is this record's own fact, not provenance,
            so it keeps its own line; who made / last touched the row is the
            kit's audit line in the footer. */}
        <div className="px-1 text-[11.5px] text-slate-400">
          {t.appointment_id ? `จากนัดหมาย #${t.appointment_id}` : 'ไม่ได้มาจากนัดหมาย'}
        </div>
      </div>

      {armDel && (
        <ConfirmDialog
          tone="danger"
          title="ลบการรักษานี้?"
          message={`การรักษาวันที่ ${fmtDate(t.date)}${branch ? ` · ${branch}` : ''}${docName ? ` · ${docName}` : ''} (#${tid}) จะหายจากประวัติการรักษาของคนไข้`}
          confirmLabel="ลบการรักษา"
          busy={deleting}
          onConfirm={doDelete}
          onCancel={() => setArmDel(false)}
        />
      )}
    </Drawer>
  )
}

// api.js throws `HTTP <status>: <body>` — dig the Thai sentence back out.
function humanErr(e) {
  const raw = String(e?.message || '')
  if (e?.status === 403) return 'บัญชีนี้ไม่มีสิทธิ์แก้ไขรายการนี้'
  const m = raw.match(/\{[\s\S]*\}\s*$/)
  if (m) {
    try {
      const j = JSON.parse(m[0])
      const msg = j.detail || j.message || j.error
      if (msg) return String(msg)
    } catch { /* not JSON */ }
  }
  return raw || 'บันทึกไม่สำเร็จ'
}

// Whole baht via the kit, rounded with Math.round first so a .5 figure reads
// exactly as BillingTable (lib/billing.js fmtBill) prints it on the card.
const fmt = (n) => fmtMoney(n == null || n === '' ? n : Math.round(Number(n)))

function Stat({ label, value, color, strong }) {
  return (
    <div>
      <div className="text-[12px] text-slate-400 mb-0.5">{label}</div>
      <div className={`tabular-nums ${strong ? 'text-[18px] font-bold' : 'text-[15px] font-semibold'}`}
        style={{ color: color || '#0f172a' }}>{fmt(value)}</div>
    </div>
  )
}
